<script setup>
import { computed } from 'vue'
import { Badge } from '@drama/ui'
import { ExternalLink } from '@lucide/vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  trace: { type: Object, required: true },
  resources: { type: Array, required: true },
})

const { t, te } = useI18n()
const resourcesById = computed(() => new Map(props.resources.map(resource => [resource.id, resource])))

function statusVariant(status) {
  switch (status) {
    case 'passed':
    case 'ok':
    case 'available':
    case 'selected':
      return 'success'
    case 'error':
      return 'danger'
    case 'needs_llm_selection':
    case 'rejected':
    case 'stopped':
      return 'warning'
    case 'empty':
    case 'below_minimum':
    case 'miss':
    case 'not_recorded':
    case 'uncovered':
      return 'neutral'
    default:
      return 'neutral'
  }
}

function has(step, field) {
  return Object.hasOwn(step, field)
}

function resource(resourceId) {
  const item = resourcesById.value.get(resourceId)
  if (item === undefined) throw new Error(`Missing video resource in match detail: ${resourceId}`)
  return item
}

function isSelected(step, resourceId) {
  return has(step, 'selected_resource_id') && step.selected_resource_id === resourceId
}

function callCapability(step) {
  switch (step.kind) {
    case 'semantic_rerank':
    case 'intent_llm_selection':
      return 'text'
    case 'semantic_embedding':
    case 'intent_embedding':
    case 'intent_demand_embedding':
      return 'embedding'
    default:
      return null
  }
}

function stepLabel(kind) {
  const key = `visualGameAdmin.llm.videoMatch.steps.${kind}`
  return te(key) ? t(key) : kind
}

function statusLabel(status) {
  const key = `visualGameAdmin.llm.videoMatch.status.${status}`
  return te(key) ? t(key) : status
}

function formatSimilarity(value) {
  return Number(value).toFixed(4)
}

function formatFilter(filter) {
  return Object.entries(filter).map(([key, value]) => `${key}: ${value}`).join(' · ')
}
</script>

<template>
  <ol class="match-trace" data-testid="video-match-trace">
    <li v-for="(step, index) in trace.steps" :key="`${index}-${step.kind}`" class="match-trace__step">
      <div class="match-trace__rail" aria-hidden="true">
        <span>{{ index + 1 }}</span>
      </div>

      <article class="match-trace__card">
        <header class="match-trace__header">
          <div>
            <span class="match-trace__eyebrow">{{ t('visualGameAdmin.llm.videoMatch.stepNumber', { number: index + 1 }) }}</span>
            <h3>{{ stepLabel(step.kind) }}</h3>
          </div>
          <Badge :variant="statusVariant(step.status)" size="sm">
            {{ statusLabel(step.status) }}
          </Badge>
        </header>

        <p v-if="has(step, 'reason')" class="match-trace__reason">{{ step.reason }}</p>
        <p v-if="has(step, 'filter')" class="match-trace__filter">{{ formatFilter(step.filter) }}</p>
        <p v-if="has(step, 'minimum_similarity_threshold')" class="match-trace__threshold">
          {{ t('visualGameAdmin.llm.videoMatch.minimumThreshold', { value: formatSimilarity(step.minimum_similarity_threshold) }) }}
        </p>
        <p v-if="has(step, 'direct_match_threshold')" class="match-trace__threshold">
          {{ t('visualGameAdmin.llm.videoMatch.directThreshold', { value: formatSimilarity(step.direct_match_threshold) }) }}
        </p>

        <RouterLink
          v-if="has(step, 'call_id') && callCapability(step) !== null"
          class="match-trace__call-link"
          :to="{
            name: 'llm-calls',
            query: { capability: callCapability(step), request_id: step.call_id },
          }"
        >
          {{ t('visualGameAdmin.llm.videoMatch.openCall', { id: step.call_id }) }}
          <ExternalLink :size="14" />
        </RouterLink>

        <div v-if="has(step, 'candidates') && step.candidates.length > 0" class="match-trace__candidates">
          <div
            v-for="candidate in step.candidates"
            :key="candidate.resource_id"
            class="match-trace__candidate"
            :class="{ 'is-selected': isSelected(step, candidate.resource_id) }"
          >
            <div class="match-trace__candidate-head">
              <code>{{ candidate.resource_id }}</code>
              <span>{{ t('visualGameAdmin.llm.videoMatch.similarity', { value: formatSimilarity(candidate.similarity) }) }}</span>
              <Badge v-if="isSelected(step, candidate.resource_id)" variant="success" size="sm">
                {{ t('visualGameAdmin.llm.videoMatch.selectedCandidate') }}
              </Badge>
            </div>
            <p>{{ resource(candidate.resource_id).semantic_text }}</p>
            <a
              v-if="resource(candidate.resource_id).video_url !== null"
              :href="resource(candidate.resource_id).video_url"
              target="_blank"
              rel="noreferrer"
            >
              {{ t('visualGameAdmin.llm.videoMatch.openVideo') }}
              <ExternalLink :size="14" />
            </a>
          </div>
        </div>

        <div
          v-else-if="has(step, 'resource_id') && step.resource_id !== null"
          class="match-trace__selected-resource"
        >
          <code>{{ step.resource_id }}</code>
          <p>{{ resource(step.resource_id).semantic_text }}</p>
        </div>
      </article>
    </li>
  </ol>
</template>

<style scoped>
.match-trace {
  width: min(100%, 980px);
  margin: 0 auto;
  padding: 0;
  list-style: none;
}

.match-trace__step {
  display: grid;
  grid-template-columns: var(--drama-space-xl) minmax(0, 1fr);
  gap: var(--drama-space-md);
}

.match-trace__rail {
  position: relative;
  display: flex;
  justify-content: center;
}

.match-trace__rail::after {
  position: absolute;
  top: var(--drama-space-xl);
  bottom: 0;
  width: 2px;
  background: var(--drama-color-border);
  content: '';
}

.match-trace__step:last-child .match-trace__rail::after {
  display: none;
}

.match-trace__rail span {
  z-index: 1;
  display: grid;
  width: var(--drama-space-xl);
  height: var(--drama-space-xl);
  place-items: center;
  border: 2px solid var(--drama-color-primary);
  border-radius: var(--drama-radius-full);
  background: var(--drama-color-surface);
  color: var(--drama-color-primary);
  font-size: var(--drama-font-size-xs);
  font-weight: var(--drama-font-weight-bold);
}

.match-trace__card {
  display: grid;
  gap: var(--drama-space-md);
  margin-bottom: var(--drama-space-lg);
  padding: var(--drama-space-lg);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-lg);
  background: var(--drama-color-surface);
  box-shadow: var(--drama-shadow-sm);
}

.match-trace__header,
.match-trace__candidate-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--drama-space-md);
}

.match-trace__eyebrow {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.match-trace__header h3 {
  margin: var(--drama-space-xs) 0 0;
  font-size: var(--drama-font-size-lg);
}

.match-trace__reason,
.match-trace__filter,
.match-trace__threshold,
.match-trace__selected-resource p,
.match-trace__candidate p {
  margin: 0;
  line-height: 1.6;
}

.match-trace__filter,
.match-trace__threshold {
  color: var(--drama-color-text-muted);
  font-family: var(--drama-font-family-mono);
  font-size: var(--drama-font-size-xs);
  overflow-wrap: anywhere;
}

.match-trace__call-link,
.match-trace__candidate a {
  display: inline-flex;
  align-items: center;
  gap: var(--drama-space-xs);
  width: fit-content;
  color: var(--drama-color-primary);
  font-size: var(--drama-font-size-sm);
  text-decoration: none;
}

.match-trace__candidates {
  display: grid;
  gap: var(--drama-space-sm);
}

.match-trace__candidate,
.match-trace__selected-resource {
  display: grid;
  gap: var(--drama-space-sm);
  padding: var(--drama-space-md);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-md);
  background: var(--drama-color-surface-alt);
}

.match-trace__candidate.is-selected {
  border-color: var(--drama-color-success);
}

.match-trace__candidate-head {
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
}

.match-trace__candidate-head span {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.match-trace code {
  font-family: var(--drama-font-family-mono);
  overflow-wrap: anywhere;
}

@media (max-width: 680px) {
  .match-trace__card {
    padding: var(--drama-space-md);
  }

  .match-trace__header {
    align-items: flex-start;
  }
}
</style>
